/**
 * Jovii — PLAYER, candidate resolution.
 *
 * A thin client for the service's `/api/resolve`. The player needs it for two
 * things the catalogue cannot do for it:
 *
 *  1. **Renewing an expired play token.** Tokens are minted at serialisation time,
 *     so simply asking again — even from the service's own cache — hands back a
 *     fresh one. A `410` from the byte proxy means "re-resolve", never "retry".
 *  2. **Refilling the candidate list** when every link the page was opened with has
 *     been tried and failed.
 *
 * The catalogue UI normally passes candidates in as a prop; this module is the
 * fallback and the renewal path, not the main road.
 */

import type { Candidate, ContentRef } from '$types';

export interface ResolveResponse {
  content: ContentRef;
  candidates: Candidate[];
  count: number;
  from: 'fresh' | 'cache' | 'stale';
  ageMs: number;
  raw: number;
  upstreamMs: number;
  serverTime: number;
}

export class ResolveError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'ResolveError';
  }
}

/**
 * The invite code, if this tab was opened with one.
 *
 * `?invite=` on any `/api/*` request makes the service set its cookie, and the
 * cookie is what the room WebSocket needs (a browser cannot put a header on a
 * WebSocket handshake). Carrying it on our own calls means a shared link works
 * without a separate sign-in step.
 */
function inviteCode(): string | null {
  if (typeof location === 'undefined') return null;
  const fromUrl = new URLSearchParams(location.search).get('invite');
  if (fromUrl) {
    try {
      sessionStorage.setItem('jove.together.invite', fromUrl);
    } catch {
      /* private mode: the query parameter still works for this request */
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem('jove.together.invite');
  } catch {
    return null;
  }
}

/**
 * Exchange a `?invite=` code for the service's cookie. Safe to call more than once
 * and safe to call with no code (the service answers with `authenticated: false`
 * and the app carries on — an open instance needs no invite at all).
 */
export async function ensureSession(): Promise<boolean> {
  const code = inviteCode();
  const url = code === null ? '/api/session' : `/api/session?invite=${encodeURIComponent(code)}`;
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return false;
    const body = (await res.json()) as { authenticated?: boolean };
    return body.authenticated === true;
  } catch {
    return false;
  }
}

export interface ResolveOptions {
  /** Bypass the service's 30-minute candidate cache. */
  refresh?: boolean;
  signal?: AbortSignal;
}

/** Ask the service for candidates. Best-first; `url` is already a byte-proxy path. */
export async function resolveCandidates(
  content: ContentRef,
  options: ResolveOptions = {}
): Promise<ResolveResponse> {
  const params = new URLSearchParams();
  if (options.refresh) params.set('refresh', '1');
  const code = inviteCode();
  if (code !== null) params.set('invite', code);
  const query = params.toString();

  const res = await fetch(`/api/resolve${query ? `?${query}` : ''}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(content),
    ...(options.signal ? { signal: options.signal } : {})
  });

  if (!res.ok) {
    let code = `http-${res.status}`;
    let message = `the service answered ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body.error) code = body.error;
      if (body.message) message = body.message;
    } catch {
      /* non-JSON error body */
    }
    if (res.status === 401) message = 'this instance needs an invite code';
    throw new ResolveError(code, message, res.status);
  }

  const body = (await res.json()) as ResolveResponse;
  if (!Array.isArray(body.candidates)) {
    throw new ResolveError('bad-response', 'the service returned no candidate list', 200);
  }
  return body;
}

/**
 * A fresh play token for one candidate.
 *
 * Asks the cheap way first (the service's cache still mints new tokens), and only
 * forces an upstream query if the candidate has genuinely gone. Returns null when
 * this candidate no longer exists at all — the caller should move to the next one.
 */
export async function renewCandidateUrl(
  content: ContentRef,
  candidateId: string
): Promise<string | null> {
  for (const refresh of [false, true]) {
    let list: Candidate[];
    try {
      list = (await resolveCandidates(content, { refresh })).candidates;
    } catch {
      return null;
    }
    const hit = list.find((c) => c.id === candidateId);
    if (hit) return hit.url;
  }
  return null;
}
